import { api } from '../api/client';

/**
 * Opening a rendered report preview in a new tab.
 *
 * **Why this exists.** Preview used to build a `blob:` object URL from the
 * render response, open THAT in a new tab, and revoke it 60 s later. The
 * browser's built-in PDF viewer re-fetches its own URL when the user clicks
 * Download — by then the blob was revoked (or the SPA had navigated, which
 * auto-revokes every object URL the document owns), so the download failed with
 * a network error ("Check internet connection") and was named after the blob's
 * GUID, because a `blob:` URL carries no `Content-Disposition`. (User report
 * 2026-08-06.)
 *
 * The render endpoints now accept `?deliver=token` and return a short-lived
 * token instead of the PDF bytes; the tab opens a REAL API URL that stays
 * fetchable for the token's whole TTL and serves a proper filename. Download
 * then works no matter what the SPA does afterwards.
 */

/** What a render endpoint returns under `?deliver=token`. */
export interface PreviewTicket {
  /** Opaque capability token — the whole authorization for the preview URL. */
  token: string;
  /** Root-relative preview path, e.g. `/api/v1/reporting/preview/<token>`. */
  url: string;
  /** Filename the browser offers on Download. */
  fileName: string;
  /** How long the link stays fetchable. */
  expiresInSeconds: number;
}

/** Query params to add to a render POST so it returns a {@link PreviewTicket}
 *  instead of PDF bytes. Spread into an existing `params` object. */
export const PREVIEW_AS_TOKEN = { deliver: 'token' } as const;

/** Narrows a render response body to its preview ticket, or `null` when the
 *  endpoint answered with something else (e.g. an older API build still
 *  streaming bytes). */
export function previewTicketOf(body: unknown): PreviewTicket | null {
  const data = (body as { data?: Partial<PreviewTicket> } | undefined)?.data;
  return typeof data?.token === 'string' && data.token.length > 0
    ? {
        token: data.token,
        url: data.url ?? `/api/v1/reporting/preview/${data.token}`,
        fileName: data.fileName ?? 'Report.pdf',
        expiresInSeconds: data.expiresInSeconds ?? 900,
      }
    : null;
}

/** Absolute URL for a preview ticket — resolved against the same API base the
 *  shared axios client uses, so it works both in the Vite dev server (separate
 *  origin) and in the production same-origin build. */
export function previewUrl(ticket: PreviewTicket): string {
  const base = (api.defaults.baseURL ?? '').replace(/\/+$/, '');
  return `${base}${ticket.url}`;
}

/**
 * Opens a rendered preview in a new tab.
 *
 * @param body The render response body (`res.data`) from a POST made with
 *             {@link PREVIEW_AS_TOKEN}.
 * @returns `true` when a tab was opened; `false` when the response carried no
 *          ticket or the browser blocked the pop-up — callers surface their own
 *          message in that case.
 */
export function openReportPreview(body: unknown): boolean {
  const ticket = previewTicketOf(body);
  if (!ticket) return false;
  return window.open(previewUrl(ticket), '_blank') != null;
}
